import { inject, Injectable } from '@angular/core';
import { Config } from '@luggagehero/shared/environment';
import { SharedStorageService, StorageTarget } from '@luggagehero/shared/services/storage';
import { BehaviorSubject, Observable } from 'rxjs';

import { SharedAppSettings } from './shared-app-settings';
import {
  describeUrlSettingOverrideRejection,
  hasUrlSettingOverrideClearParam,
  parseUrlSettingOverrideParams,
  RawUrlSettingOverride,
  resolveUrlSettingOverrides,
  URL_SETTING_OVERRIDE_PREFIX,
  URL_SETTING_OVERRIDES_ENABLED_SETTING,
} from './url-setting-overrides';

/**
 * Session-storage key holding the raw `lh_ft_*` overrides for this tab.
 *
 * Kept here rather than as a named accessor on `SharedStorageService` so app-settings semantics stay in
 * the app-settings lib — the storage service's generic `get`/`set`/`remove` is public API and enough.
 */
const URL_SETTING_OVERRIDES_KEY = 'url_setting_overrides';

/**
 * Holds the per-session URL setting overrides (AM-4658) and applies them on top of the assembled app
 * settings.
 *
 * **Session-scoped on purpose.** The variant override (`?lh_variant=`) persists in local storage because
 * an experiment assignment must survive a tab close. This is the opposite: an override exists to look at
 * something once, so it self-clears when the tab does. A flag silently forced three days after someone
 * clicked a link is exactly the unreproducible-bug-report failure this is meant to avoid.
 */
@Injectable({ providedIn: 'root' })
export class UrlSettingOverridesService {
  private storage = inject(SharedStorageService);

  private readonly activeSubject = new BehaviorSubject<Record<string, unknown>>({});

  /**
   * The overrides currently in force, keyed by setting name and already coerced.
   *
   * Empty until the first {@link apply} call — the gate depends on the settings payload, so whether an
   * override is in force is not knowable at capture time.
   */
  public readonly active$: Observable<Record<string, unknown>> = this.activeSubject.asObservable();

  /** The overrides currently in force. Empty when none apply. */
  public get active(): Record<string, unknown> {
    return this.activeSubject.value;
  }

  /** Whether any override is currently in force — the indicator's visibility condition. */
  public get hasActiveOverrides(): boolean {
    return Object.keys(this.activeSubject.value).length > 0;
  }

  /**
   * Reads `lh_ft_*` params off the URL and records them for the session.
   *
   * Must run **before** `SharedAppSettingsService.init()`, because that is what assembles the settings
   * the overrides are then applied to. Params are stored raw and uncoerced: the target types come from
   * the assembled settings, which do not exist yet at this point.
   *
   * Overrides accumulate across navigations within the session, so successive links each add a flag
   * rather than replacing the previous one; `?lh_ft_clear=1` is the reset.
   *
   * @param params The query params from the current URL.
   */
  public capture(params: Record<string, string> | undefined): void {
    if (hasUrlSettingOverrideClearParam(params)) {
      this.clear();
      return;
    }

    const parsed = parseUrlSettingOverrideParams(params);
    if (parsed.length === 0) {
      return;
    }

    const stored = this.read();
    for (const override of parsed) {
      stored[override.name] = override.rawValue;
    }

    this.write(stored);
  }

  /** Drops every override for this session, and the active set with it. */
  public clear(): void {
    this.storage.remove(URL_SETTING_OVERRIDES_KEY, StorageTarget.Session);
    this.activeSubject.next({});
  }

  /**
   * Applies the session's overrides on top of the assembled settings, mutating them in place.
   *
   * Called as the last step of settings assembly so the resulting precedence is
   * **URL override → server row → app default**, and so a settings refresh re-applies rather than
   * silently dropping the override.
   *
   * Values are coerced to the type of the value they replace, because `AppConfig.override` discards a
   * type mismatch without a word — see {@link coerceToSettingType}.
   *
   * @param settings The assembled settings to apply the overrides to.
   */
  public apply(settings: SharedAppSettings): void {
    const stored = this.read();
    const names = Object.keys(stored);

    if (names.length === 0) {
      if (this.hasActiveOverrides) {
        this.activeSubject.next({});
      }
      return;
    }

    if (!this.isEnabled(settings)) {
      // Production with the switch off (the default): ignore the params entirely rather than warn per
      // key. A warning here would be noise on any traveler who happened to follow a stale link.
      if (this.hasActiveOverrides) {
        this.activeSubject.next({});
      }
      return;
    }

    const raw: RawUrlSettingOverride[] = names.map((name) => ({ name, rawValue: stored[name] }));
    const { applied, rejected } = resolveUrlSettingOverrides(raw, settings);

    for (const rejection of rejected) {
      // Warn loudly and name the key. A silent no-op is the worst outcome: the person testing sees the
      // unchanged app and concludes the feature is broken rather than that they mistyped the param.
      console.warn(`[${URL_SETTING_OVERRIDE_PREFIX}] ${describeUrlSettingOverrideRejection(rejection)}`);
    }

    for (const name of Object.keys(applied)) {
      settings[name] = applied[name];
    }

    if (Object.keys(applied).length > 0) {
      console.warn(
        `[${URL_SETTING_OVERRIDE_PREFIX}] App settings overridden for this session:`,
        applied,
        `— clear with ?${URL_SETTING_OVERRIDE_PREFIX}clear=1`,
      );
    }

    this.activeSubject.next(applied);
  }

  /**
   * Whether the mechanism is enabled for this environment.
   *
   * Non-production is always on — the whole point is a low-friction way to look at a variation on
   * staging or in a demo. Production is gated on the `urlSettingOverridesEnabled` **app setting** rather
   * than a compile-time `Config.isProduction` check, because the one time you want it in production is
   * to reproduce something that only happens there; a stored row can be flipped on for an hour and off
   * again, a build constant cannot. The setting itself is deliberately not overridable, so the gate
   * cannot be opened by the very params it gates.
   */
  private isEnabled(settings: SharedAppSettings): boolean {
    if (!Config.isProduction) {
      return true;
    }
    return settings[URL_SETTING_OVERRIDES_ENABLED_SETTING] === true;
  }

  private read(): Record<string, string> {
    const stored = this.storage.get(URL_SETTING_OVERRIDES_KEY, StorageTarget.Session);
    if (!stored) {
      return {};
    }

    try {
      const parsed = JSON.parse(stored) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }

      // Normalize to strings — the stored shape is raw query-param values, and a hand-edited or
      // stale-format entry must not leak a non-string into coercion.
      const result: Record<string, string> = {};
      for (const [name, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof value === 'string') {
          result[name] = value;
        }
      }
      return result;
    } catch {
      // A corrupt entry is not worth failing app bootstrap over — drop it and carry on unoverridden.
      return {};
    }
  }

  private write(overrides: Record<string, string>): void {
    this.storage.set(URL_SETTING_OVERRIDES_KEY, JSON.stringify(overrides), StorageTarget.Session);
  }
}
